import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { documentService, type Document, type DocumentListResponse, type ShareSettings } from './document-service'

export const documentKeys = {
  all: ['documents'] as const,
  list: (page: number) => ['documents', 'list', page] as const,
  detail: (id: string) => ['documents', 'detail', id] as const,
  versions: (id: string) => ['documents', 'versions', id] as const,
}

export function useDocumentList(page: number) {
  return useQuery<DocumentListResponse>({
    queryKey: documentKeys.list(page),
    queryFn: () => documentService.list(page),
    placeholderData: (prev) => prev,
  })
}

export function useDocument(id: string) {
  return useQuery<Document>({
    queryKey: documentKeys.detail(id),
    queryFn: () => documentService.get(id),
    enabled: !!id,
  })
}

export function useCreateDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => documentService.create(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.all })
    },
  })
}

export function useDeleteDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => documentService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.all })
    },
  })
}

export function useUpdateDocument(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Partial<Document>) => documentService.update(id, payload),
    onSuccess: (doc) => {
      qc.setQueryData(documentKeys.detail(id), doc)
      qc.invalidateQueries({ queryKey: documentKeys.all })
    },
  })
}

export function useShareDocument(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (settings: ShareSettings) => documentService.share(id, settings),
    onSuccess: (doc) => {
      qc.setQueryData(documentKeys.detail(id), doc)
    },
  })
}

export function useDocumentVersions(id: string, enabled = true) {
  return useQuery({
    queryKey: documentKeys.versions(id),
    queryFn: () => documentService.versions(id),
    enabled: enabled && !!id,
  })
}

export function useRestoreDocumentVersion(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (versionId: number) => documentService.restoreVersion(id, versionId),
    onSuccess: (doc) => {
      qc.setQueryData(documentKeys.detail(id), doc)
      qc.invalidateQueries({ queryKey: documentKeys.versions(id) })
    },
  })
}
